import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { MaturityFrameworkForm } from '../../../components/maturity';
import { createMaturityFramework, ApiError } from '../../../lib/apiClient';
import { LIST_MATURITY_FRAMEWORKS } from '../../../graphql/queries';
import type { CreateMaturityFrameworkFormData } from '../../../components/forms/validation';
import { useAppSelector } from '../../../store';
import { selectUser } from '../../../store/features/auth/authSlice';
import { hasPermission } from '../../../lib/permissions';
import '@tylertech/forge/banner';
import './new.css';

export const Route = createFileRoute('/maturity/frameworks/new')({
  component: NewMaturityFrameworkPage,
});

export { NewMaturityFrameworkPage };

function NewMaturityFrameworkPage(): JSX.Element {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const user = useAppSelector(selectUser);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const BannerElement = 'forge-banner' as React.ElementType;

  if (!hasPermission(user, 'maturityFramework:create')) {
    void navigate({ to: '/maturity/frameworks' });
    return <></>;
  }

  const handleSubmit = async (data: CreateMaturityFrameworkFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const created = await createMaturityFramework({
        name: data.name,
        description: data.description,
        version: data.version,
        status: data.status,
        effectiveFrom: data.effectiveFrom,
        effectiveTo: data.effectiveTo,
      });

      await apolloClient.refetchQueries({ include: [LIST_MATURITY_FRAMEWORKS] });

      void navigate({
        to: '/maturity/frameworks/$frameworkId',
        params: { frameworkId: created.entityId },
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create framework';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({ to: '/maturity/frameworks' });
  };

  return (
    <div className="new-framework-page">
      <div className="page-header page-header--toolbar page-header--center">
        <h1 className="forge-typography--headline5">Create Maturity Framework</h1>
      </div>

      {error && (
        <BannerElement theme="danger">
          <span>{error}</span>
        </BannerElement>
      )}

      <MaturityFrameworkForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
